import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { SITE } from "../data/site";
import { OpenStatus } from "./OpenStatus";
import { useLang } from "../i18n";

export const Footer = () => {
  const { t } = useLang();
  return (
    <footer data-testid="site-footer" className="grain relative border-t border-ink/10 bg-espresso text-ink">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 pt-16 sm:pt-24 pb-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4">
              <span className="aura-logo inline-flex">
                <img
                  src={SITE.logo}
                  alt="Next-G Phone Repair logo"
                  className="h-14 w-14 rounded-full object-cover ring-1 ring-gold/40"
                />
              </span>
              <div>
                <p className="font-display text-2xl font-semibold tracking-tight">{SITE.name}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.24em] text-ink/50">
                  {SITE.category}
                </p>
              </div>
            </div>
            <p className="mt-6 font-display text-2xl sm:text-3xl font-light italic text-gold-soft">
              “{t("footer.motto")}”
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {SITE.languages.map((l) => (
                <span
                  key={l.code}
                  data-testid={`footer-lang-${l.code.toLowerCase()}`}
                  className={`rounded-full border border-ink/20 px-3 py-1 text-xs text-ink/75 ${
                    l.ethiopic ? "font-ethiopic" : ""
                  }`}
                >
                  {l.label}
                </span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <p className="eyebrow !text-gold-soft">{t("footer.explore")}</p>
            <ul className="mt-5 space-y-3 text-sm">
              {[
                { to: "/", label: t("nav.home"), id: "footer-link-home" },
                { to: "/quote", label: t("footer.quote"), id: "footer-link-quote" },
                { to: "/repair-lab", label: t("nav.lab"), id: "footer-link-repair-lab" },
                { to: "/phones", label: t("nav.phones"), id: "footer-link-phones" },
                { to: "/showroom", label: t("nav.showroom"), id: "footer-link-showroom" },
                { to: "/visit", label: t("nav.visit"), id: "footer-link-visit" },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={l.id}
                    className="text-ink/70 transition-colors hover:text-gold-soft"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="eyebrow !text-gold-soft">{t("footer.talk")}</p>
            <ul className="mt-5 space-y-3 text-sm">
              <li>
                <a
                  href={SITE.phoneHref}
                  data-testid="footer-phone-link"
                  className="group inline-flex items-center gap-2 text-ink/70 transition-colors hover:text-gold-soft"
                >
                  <Phone className="h-4 w-4 text-accent-soft" /> {SITE.phoneIntl}
                </a>
              </li>
              <li>
                <a
                  href={SITE.emailHref}
                  data-testid="footer-email-link"
                  className="inline-flex items-center gap-2 break-all text-ink/70 transition-colors hover:text-gold-soft"
                >
                  <Mail className="h-4 w-4 shrink-0 text-accent-soft" /> {SITE.email}
                </a>
              </li>
              <li>
                <a
                  href={SITE.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-map-link"
                  className="group inline-flex items-start gap-2 text-ink/70 transition-colors hover:text-gold-soft"
                >
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-accent-soft" />
                  <span>
                    {SITE.address}
                    <span className="block text-ink/45">{SITE.locationCue}</span>
                  </span>
                  <ArrowUpRight className="mt-0.5 h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
                </a>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="eyebrow !text-gold-soft">{t("footer.hours")}</p>
            <ul className="mt-5 space-y-3 text-sm text-ink/70">
              {SITE.hours.map((h) => (
                <li key={h.days} data-testid={`footer-hours-${h.days === "Sunday" ? "sunday" : "weekday"}`}>
                  <span className="block">{h.days}</span>
                  <span className="font-mono text-xs text-ink/55">{h.time}</span>
                </li>
              ))}
            </ul>
            <div className="mt-5">
              <OpenStatus />
            </div>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-3 border-t border-ink/12 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink/45">
            © {new Date().getFullYear()} {SITE.name} · {t("footer.rights")}
          </p>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink/45">
            {t("footer.line")}
          </p>
        </div>
      </div>
    </footer>
  );
};
